import { z } from 'zod';

/**
 * The Snappy identity surface: MoonHub.API `GET /v2/snappy/user` and the
 * factions launch admission / voucher check. Pinned to the fields the web
 * reads; unknown fields pass through untouched.
 */

/** Mongo dates arrive as ISO strings; the app works in ms epochs. */
const epochMs = z
    .union([z.string(), z.number()])
    .transform((value) =>
        typeof value === 'number' ? value : Date.parse(value),
    )
    .pipe(z.number().finite());

export const snappyBackupSummarySchema = z.object({
    /** lowercase on the wire; compare case-insensitively */
    address: z.string(),
    createdAt: epochMs,
    updatedAt: epochMs,
});

export const snappyIdentitySchema = z.object({
    accountHash: z.string(),
    provider: z.string(),
    displayName: z.string().nullish(),
    username: z.string().nullish(),
    current: z.boolean().optional(),
});

export const snappyBackupHistoryEntrySchema = z.object({
    address: z.string(),
    at: epochMs,
});

/**
 * `GET /v2/snappy/user`. `backups` (every slot, newest first) is absent on an
 * API that predates several backups per user; the single `backup` then stands
 * in for it.
 */
export const snappyUserSchema = z
    .object({
        userId: z.string(),
        identities: z.array(snappyIdentitySchema).default([]),
        backup: snappyBackupSummarySchema.nullish(),
        backups: z.array(snappyBackupSummarySchema).optional(),
        backupHistory: z.array(snappyBackupHistoryEntrySchema).default([]),
    })
    .transform(({ backup, backups, ...rest }) => ({
        ...rest,
        backups: backups ?? (backup ? [backup] : []),
    }));

/**
 * factions `GET /speed/snappy/admission` and `POST .../admission/redeem`:
 * `enabled` is the launch switch, `admitted` whether this account may set up a
 * browser wallet (always true while the switch is off).
 */
export const snappyAdmissionSchema = z.object({
    enabled: z.boolean(),
    admitted: z.boolean(),
});

/** factions `POST /speed/onboarding/voucher/check`. Spends nothing. */
export const voucherCheckSchema = z.object({
    valid: z.boolean(),
    flow: z.enum(['v1', 'v2']),
});

/**
 * `GET /v2/snappy/backup/blob?address=` → `{ blob }`. The blob is the
 * client-encrypted backup; its format belongs to the vendored engine
 * (`@snappy/core/lib/wallet/backup` `parseBackupValue`), so only "a JSON
 * object" is pinned here.
 */
export const snappyBackupBlobResponseSchema = z.object({
    blob: z.record(z.string(), z.unknown()),
});

/**
 * `PUT /v2/snappy/backup { blob, replace? }` → `{ backup, backups,
 * backupHistory }`: every slot after the write, newest first.
 */
export const snappyPutBackupResponseSchema = z
    .object({
        backup: snappyBackupSummarySchema.nullish(),
        backups: z.array(snappyBackupSummarySchema).optional(),
        backupHistory: z.array(snappyBackupHistoryEntrySchema).default([]),
    })
    .transform(({ backup, backups, backupHistory }) => ({
        backups: backups ?? (backup ? [backup] : []),
        backupHistory,
    }));

/**
 * `DELETE /v2/snappy/backup?address=` → `{ deleted: true, address }`;
 * idempotent: `address` is null when there was nothing to delete.
 */
export const snappyDeleteBackupResponseSchema = z.object({
    deleted: z.boolean(),
    address: z.string().nullable().default(null),
});

/** The `{ error, code }` body both backends answer a refusal with. */
export const snappyErrorBodySchema = z
    .object({
        error: z.string().optional(),
        code: z.string().optional(),
    })
    .passthrough();
